import { useLocation, useNavigate } from "react-router-dom";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth, type User } from "@/contexts/AuthContext";
import { apiClient, AuthError } from "@/lib/api-client";
import { loginRedirectTarget } from "@/lib/login-redirect";

interface AuthResponse {
  user: User;
}

interface LoginPayload {
  email: string;
  password: string;
}

interface RegisterPayload {
  companyName: string;
  inn: string;
  contactName: string;
  phone: string;
  email: string;
  password: string;
}

interface VerifyOtpPayload {
  email: string;
  code: string;
}

/**
 * Authenticates the user with email and password.
 * On success, updates global auth state and returns to the page that sent the user to
 * /login (see ProtectedRoute), or /account.
 */
export function useLogin() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  return useMutation<AuthResponse, Error, LoginPayload>({
    // A 401 carries the neutral "session expired" wording by default; on this form it can
    // only mean the credentials were wrong.
    mutationFn: (payload) =>
      apiClient.post<AuthResponse>("/auth/login", payload).catch((error: unknown) => {
        if (error instanceof AuthError && !error.fromServer) {
          throw new AuthError("Неверный email или пароль");
        }
        throw error;
      }),
    onSuccess: ({ user }) => {
      setUser(user);
      navigate(loginRedirectTarget(location.state), { replace: true });
    },
  });
}

/**
 * Creates a new B2B account.
 * On success, redirects to /verify-email with the email in navigation state.
 */
export function useRegister() {
  const navigate = useNavigate();

  return useMutation<{ message: string }, Error, RegisterPayload>({
    mutationFn: (payload) => apiClient.post<{ message: string }>("/auth/register", payload),
    onSuccess: (_, variables) => {
      navigate("/verify-email", { state: { email: variables.email } });
    },
  });
}

/**
 * Verifies the email OTP sent after registration.
 * On success, updates global auth state and redirects to /account.
 */
export function useVerifyOtp() {
  const { setUser } = useAuth();
  const navigate = useNavigate();

  return useMutation<AuthResponse, Error, VerifyOtpPayload>({
    mutationFn: (payload) => apiClient.post<AuthResponse>("/auth/verify-otp", payload),
    onSuccess: ({ user }) => {
      setUser(user);
      navigate("/account");
    },
  });
}

/**
 * Resends the verification OTP to the given email address.
 */
export function useResendOtp() {
  return useMutation<{ message: string }, Error, { email: string }>({
    mutationFn: (payload) => apiClient.post<{ message: string }>("/auth/resend-otp", payload),
  });
}

/**
 * Logs out the current user.
 * Clears all React Query caches, nullifies auth state, and redirects to /login.
 */
export function useLogout() {
  const { setUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<unknown, Error, void>({
    mutationFn: () => apiClient.post<unknown>("/auth/logout", {}),
    onSettled: () => {
      setUser(null);
      queryClient.clear();
      navigate("/login");
    },
  });
}
